/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getTiposDeContrataciones,
  postTipoDeContratacion,
  deleteTipoDeContratacion,
  patchTipoDeContratacion,
  getTipoDeContratacion,
} from 'api/catalogos/tiposDeContrataciones';
import { getTiposContratosNominas } from 'api/catalogos/tiposContratosNominas';
import { checkToken } from 'api/utils';
import {
  SLUG_PATTERN,
} from 'utils/patterns';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_INACTIVO,
}
  from 'utils/constants';
import { CheckCircleOutlined } from '@ant-design/icons';
import ModalConfirmation from 'components/ModalConfirmation';

export const tipodecontratacionPermission = {
  permissionModel: 'tipodecontratacion',
  catalogsModels: [],
  tabsModels: [],
};

const TipoDeContratacion = ({ permission }) => {
  const [form] = Form.useForm();
  const [formDatos] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [tiposContratosNominas, setTiposContratosNominas] = useState([]);
  const [disableAuth, setDisableAuth] = useState(false);
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const [disableForm, setDisableForm] = useState(false);
  const [visibleAuthConfirmation, setVisibleAuthConfirmation] = useState(false);
  const [visibleAlertInactive, setVisibleAlertInactive] = useState(false);

  const formInstances = [
    form,
    formDatos,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTiposDeContrataciones();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _tiposContratosNominas,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getTiposContratosNominas(),
        getTiposDeContrataciones(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setTiposContratosNominas(_tiposContratosNominas);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    setDisableForm(false);
    form.resetFields();
    formDatos.resetFields();
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede Autorizar un Sindicato ya Autorizado');
        return;
      }
      const vals = {
        ...form.getFieldValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchTipoDeContratacion(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleAuthConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const isDisabled = (rowEstatus) => {
    if (rowEstatus !== ESTATUS_ACTIVO) {
      setDisableForm(true);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _tipoDeContratacion = await getTipoDeContratacion(match.id);
    setTimeout(() => {
      if (_tipoDeContratacion) {
        form.resetFields();
        formDatos.resetFields();
        setTimeout(() => {
          isDisabled(_tipoDeContratacion?.estados_globales);
          form.setFieldsValue(formatReceived(_tipoDeContratacion));
          formDatos.setFieldsValue(formatReceived(_tipoDeContratacion));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formDatos.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formDatos.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchTipoDeContratacion(key, values) : postTipoDeContratacion(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteTipoDeContratacion(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const isAutorizado = record?.estados_globales === ESTATUS_AUTORIZADO;
    const isInactivo = record?.estados_globales === ESTATUS_INACTIVO;
    setDeleteDisabled(isAutorizado);
    setDisableAuth(isAutorizado || isInactivo);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 100,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
    },
    {
      titleText: 'Tipo de Contrato SAT',
      dataIndex: 'tipo_de_contrato_sat',
      key: 'tipo_de_contrato_sat',
      width: 300,
      render: (val) => {
        const _tiposContratosNominas = tiposContratosNominas.find((e) => e.id === val);
        return `${_tiposContratosNominas.clave} - ${_tiposContratosNominas.descripcion}`;
      },
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    concepto: [requiredRule],
    clave: [
      requiredRule,
      {
        pattern: SLUG_PATTERN,
        message: 'Ingrese una clave válida',
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          allowExportPDF={false}
          customActions={[
            {
              onClick: () => setVisibleAuthConfirmation(true),
              permission: { model: 'tipodecontratacion', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              // eslint-disable-next-line max-len
              disabled: !selectedRowKeys?.length || disableAuth,
            },
          ]}
          disableDelete={
            deleteDisabled
          }
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Contratación`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!selectedRowKeys?.length || !disableAuth}
            />
          )}
          onCancel={onCancel}
          forceRender
          width="40%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            disabled={disableForm}
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="clave"
                  rules={rules.clave}
                  label="Clave"
                  hasFeedback
                >
                  <Input allowClear />
                </Form.Item>
                <Form.Item
                  name="descripcion"
                  rules={rules.concepto}
                  label="Descripción"
                  hasFeedback
                >
                  <Input.TextArea
                    allowClear
                    autoSize={{ minRows: 2, maxRows: 3 }}
                  />
                </Form.Item>
                <Form.Item
                  name="tipo_de_contrato_sat"
                  label="Tipo de Contrato SAT"
                  rules={rules.required}
                  hasFeedback
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={tiposContratosNominas}
                    render={(val) => `${val?.clave} - ${val?.descripcion}`}
                  />
                </Form.Item>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.required}
                  hasFeedback
                  initialValue={1}
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    activeInactive={!deleteDisabled}
                    dataSource={estados}
                    disabled={selectedRowKeys.length <= 0}
                    onChange={(val) => {
                      if (val === ESTATUS_INACTIVO) {
                        setVisibleAlertInactive(true);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Tipo de Contratación ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleAlertInactive(false)}
          ConfirmInactive
          onCancel={() => {
            setVisibleAlertInactive(false);
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
          }}
          open={visibleAlertInactive}
          contentTitle={`Inactivar el Tipo de Contratación ${form.getFieldValue('clave')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setVisibleAuthConfirmation(false);
            onCancel();
          }}
          open={visibleAuthConfirmation}
          contentTitle={`Autorizar el Tipo de Contratación ${form.getFieldValue('clave')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TipoDeContratacion;
